import React, { useEffect } from 'react';
import SignInForm from 'components/sign-in/SignInForm';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import MultiRoleTypesContent from 'components/sign-in/MultiRoleTypesContent';
import FormsSpinner from 'components/common/FormSpinner';
import SingleRoleTypeContent from 'components/sign-in/SingleRoleTypeContent';
import { createStyles } from '../../globalStyles';
import { ButtonStyled, MessageStyled } from './styles';

const SignedInContent: React.FC = observer(() => {
  const {
    roleTypeAndAgencyStore: { roleTypes, sendLogoutUserRequest },
  } = useRootStore();

  const convertedRoleTypes = roleTypes.map((type) => toJS(type)).slice();

  // User with no auth user type received from BE.
  // This could happen for Zywave users because they are logged in via an alternative method.
  if (convertedRoleTypes.length === 0)
    return (
      <div>
        <MessageStyled>
          You are already signed in. This probably happened via an alternative login method. If this is unexpected,
          please log out and sign in again.
        </MessageStyled>
        <ButtonStyled size="large" type="outlined" onClick={sendLogoutUserRequest}>
          Logout
        </ButtonStyled>
      </div>
    );

  if (convertedRoleTypes.length === 1) return <SingleRoleTypeContent roleType={convertedRoleTypes[0]} />;

  return <MultiRoleTypesContent />;
});

const LoginPage = observer((): JSX.Element => {
  const classes = createStyles();

  // Mobx store
  const {
    roleTypeAndAgencyStore: { roleTypes, allAgencies, loading, getInitialDataForLoggedInUser, resetInitialData },
    loginSessionStore: { isLoggedIn },
  } = useRootStore();

  // Converted Data
  const convertedRoleTypes = roleTypes.map((type) => toJS(type)).slice();
  const convertedAgencies = allAgencies.map((agency) => toJS(agency)).slice();

  useEffect(() => {
    if (isLoggedIn && (convertedAgencies.length === 0 || convertedRoleTypes.length === 0)) {
      void getInitialDataForLoggedInUser();
    }
    return resetInitialData;
  }, [isLoggedIn]);

  return (
    <div className={classes.formWrapper}>
      <h1 className={classes.title}>
        {!loading && isLoggedIn && convertedRoleTypes.length === 0 ? 'Already Signed In' : 'Sign In'}
      </h1>
      {loading && <FormsSpinner display={loading} />}
      {!loading && (isLoggedIn ? <SignedInContent /> : <SignInForm />)}
    </div>
  );
});

export default LoginPage;
